import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Phone, Clock, MapPin, Award, Wrench, Home, Shield, Droplet, Zap, CheckCircle, Users, AlertTriangle, Gauge, Scissors, Camera, User } from 'lucide-react';
import { SchemaInjector, createLocalBusinessSchema, createServiceSchema, createBreadcrumbSchema } from '../components/SchemaInjector';
import { createReviewSchema } from '../lib/schemas/aiSeoSchemas';

const LakewoodPlumber = () => {
  // Schema markup data for Lakewood
  const localBusinessSchema = createLocalBusinessSchema('Lakewood', {
    lat: '39.7047',
    lng: '-105.0814'
  });

  const waterHeaterServiceSchema = createServiceSchema(
    'Water Heater Installation - Lakewood',
    'Professional water heater installation and replacement in Lakewood, Colorado. Starting at $2,000 with transparent pricing.',
    2000
  );

  const sewerServiceSchema = createServiceSchema(
    'Sewer Camera Inspection - Lakewood',
    'Professional sewer camera scope inspection in Lakewood to diagnose issues before expensive repairs. Starting at $175.',
    175
  );

  const breadcrumbSchema = createBreadcrumbSchema([
    { name: 'Home', url: 'https://colorado-plumbing.com/' },
    { name: 'Service Areas', url: 'https://colorado-plumbing.com/#service-area' },
    { name: 'Lakewood Plumber', url: 'https://colorado-plumbing.com/lakewood-plumber' }
  ]);

  // Enhanced Organization Schema for E-E-A-T
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Colorado First Plumbing and Sewer LLC",
    "alternateName": "Colorado First Plumbing",
    "url": "https://colorado-plumbing.com",
    "logo": "https://colorado-plumbing.com/logo.png",
    "foundingDate": "2018",
    "founders": [
      {
        "@type": "Person",
        "name": "Codee Jessen",
        "jobTitle": "Master Plumber"
      },
      {
        "@type": "Person",
        "name": "Michelle Jessen",
        "jobTitle": "Owner (Majority)"
      }
    ],
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "360",
      "bestRating": "5"
    },
    "hasCredential": [
      {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "Master Plumber License",
        "recognizedBy": {
          "@type": "Organization",
          "name": "State of Colorado"
        }
      },
      {
        "@type": "EducationalOccupationalCredential",
        "credentialCategory": "BBB Accreditation",
        "recognizedBy": {
          "@type": "Organization",
          "name": "Better Business Bureau"
        },
        "dateCreated": "2024-07-15"
      }
    ]
  };

  // Comprehensive FAQPage Schema for AI Optimization - Lakewood-specific
  const faqPageSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How Much is a Sewer Camera Inspection and Clog Removal in Lakewood?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC offers affordable sewer diagnostics in Lakewood starting with a camera inspection for $175. Based on findings, standard mainline snaking from a proper cleanout is $295, while powerful hydro-jetting service is $695. This diagnostic-first approach ensures you only pay for the service you truly need."
        }
      },
      {
        "@type": "Question",
        "name": "What Are My Options for Failing Cast Iron Sewer Pipes in Lakewood?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC provides affordable solutions for Lakewood's aging cast iron pipes. Mainline descaling for $995 restores flow in structurally sound pipes, while full trenchless replacement for $12,000 addresses collapsed or severely damaged lines. Both options come with transparent, flat-rate pricing."
        }
      },
      {
        "@type": "Question",
        "name": "How Much Does Excavating a Sewer Line in a Lakewood Yard Cost?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC offers affordable sewer spot repair in Lakewood yards for a flat rate of $5,000. This all-inclusive price covers excavation, pipe repair, backfill, and restoration - providing a permanent fix for localized problems while preserving most of your landscaping."
        }
      },
      {
        "@type": "Question",
        "name": "What Are the Summer Special Prices for a Water Heater Replacement in Lakewood?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC's affordable Summer Special provides complete water heater replacement for Lakewood homeowners at $2,000 for a 40-gallon unit or $2,100 for a 50-gallon unit. These all-inclusive prices include Rheem or AO Smith units, professional installation to code, permits, and disposal."
        }
      },
      {
        "@type": "Question",
        "name": "How Much Does an Electric or Direct Vent Water Heater Cost in Lakewood?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC offers affordable alternative water heater options in Lakewood. A 40-gallon electric water heater installation is $5,395, and a 40-gallon power direct vent model is also $5,395. These flat-rate prices cover the complexity and materials for specialized installation types."
        }
      },
      {
        "@type": "Question",
        "name": "Is Upgrading to a Tankless Water Heater in Lakewood Worth the Cost?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Colorado First Plumbing and Sewer LLC provides affordable tankless water heater solutions in Lakewood at $8,000 for new installation or $5,000 for replacement. With endless hot water, 34% energy savings, and 20+ year lifespan, tankless systems offer excellent long-term value for Lakewood homes."
        }
      }
    ]
  };

  useEffect(() => {
    // SEO meta tags setup
    document.title = "Affordable Lakewood Plumber | Colorado First Plumbing | Real Prices";
    
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Lakewood\'s ONLY affordable plumber showing actual prices online. Sewer scope $175, water heater $2,000. No hidden fees. 1000+ reviews across all platforms. Master plumber owned.');
    }

    // BirdEye widget loading
    const loadBirdEyeWidget = () => {
      const existingScript = document.querySelector('#birdeye-widget-script');
      if (existingScript) return;

      const script = document.createElement('script');
      script.id = 'birdeye-widget-script';
      script.type = 'text/javascript';
      script.src = 'https://birdeye.com/embed/v7/172384402926614/8/9876543218815183';
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    };

    setTimeout(loadBirdEyeWidget, 1000);

    return () => {
      const script = document.querySelector('#birdeye-widget-script');
      if (script) script.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <SchemaInjector 
        schemas={[localBusinessSchema, organizationSchema, waterHeaterServiceSchema, sewerServiceSchema, breadcrumbSchema, faqPageSchema]}
        title="Affordable Lakewood Plumber | Colorado First Plumbing | Real Prices"
        description="Lakewood's ONLY affordable plumber showing actual prices online. Sewer scope $175, water heater $2,000. No hidden fees. 1000+ reviews across all platforms. Master plumber owned."
        canonical="https://colorado-plumbing.com/lakewood-plumber"
        keywords="affordable plumber Lakewood, transparent pricing Lakewood, honest plumber Lakewood Colorado, no hidden fees plumber, 1000 reviews Lakewood"
      />
      <Header />
      
      <main>
        {/* Hero Section with Affordable Messaging */}
        <section className="relative pt-48 pb-16 bg-gradient-to-br from-trust-primary via-authority-navy to-action-primary">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(#FFFFFF_1px,transparent_1px)] [background-size:32px_32px]"></div>
          <div className="container-custom text-center relative z-10">
            {/* Trust Signals Banner */}
            <div className="inline-block px-6 py-3 mb-6 rounded-full bg-white/10 backdrop-blur-sm border border-white/20">
              <span className="text-white font-bold">🏆 Lakewood's Most Affordable Family-Owned Plumbers • Licensed Master Plumber • <Link to="/team/michelle-jessen" className="text-blue-200 hover:text-blue-100 underline">Woman Majority Owned</Link> • 1000+ Reviews Across All Platforms</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-bold mb-6 text-white leading-tight">
              Lakewood's ONLY Affordable Plumber with Exact Online Prices
              <span className="block text-2xl md:text-3xl mt-4 text-blue-200">
                Colorado First Plumbing and Sewer LLC
              </span>
            </h1>
            
            <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
              How can others claim "affordable" without showing prices? We prove affordable means transparent - 
              see every exact price online before you call. No hidden fees, no salesmen.
            </p>

            {/* Price Preview - Clickable Cards */}
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <Link to="/tankless-water-heater-pricing" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">Tankless Replace: $5,000</span>
              </Link>
              <Link to="/flat-rate-affordable-rheem-water-heaters-in-colorado" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">40 Gal Rheem: $2,000</span>
              </Link>
              <Link to="/diagnostic-services-pricing" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">Sewer Scope: $175</span>
              </Link>
              <Link to="/sewer-replacement-spot-repair-pricing" className="bg-white/10 backdrop-blur-sm rounded-lg px-4 py-2 border border-white/20 hover:bg-white/20 transition-all duration-300">
                <span className="text-yellow-300 font-bold">Spot Repair: $5,000</span>
              </Link>
            </div>

            {/* BirdEye Widget */}
            <div className="mb-8 max-w-2xl mx-auto">
              <div id="bf-revz-widget-9876543218815183" className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20 min-h-[200px]"></div>
            </div>
            
            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              
              <Link 
                to="/pricing" 
                className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-10 py-5 rounded-full text-xl font-semibold inline-block transition-all duration-300"
              >
                💰 See All Affordable Prices
              </Link>
            </div>

            {/* Credentials */}
            <div className="mt-8 flex flex-wrap justify-center items-center gap-6 text-white/90">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>Licensed Master Plumber</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span><Link to="/team/michelle-jessen" className="text-blue-200 hover:text-blue-100 underline">Woman Majority Owned</Link></span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>18,000+ Jobs Since 2018</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span>A+ BBB Rating</span>
              </div>
            </div>
          </div>
        </section>

        {/* Trust & Authority Section - 5 Card Layout */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Why Lakewood Trusts Our Affordable Plumbing Services
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-yellow-400 mb-2">18,000+</div>
                <div className="text-lg font-semibold text-white mb-2">Jobs Completed</div>
                <p className="text-blue-100 text-sm">Since 2018 serving Lakewood, Green Mountain, Belmar, and surrounding areas</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">1000+</div>
                <div className="text-lg font-semibold text-white mb-2">Reviews Online</div>
                <p className="text-blue-100 text-sm">4.8★ average across Google (313+), BirdEye (300+), HomeAdvisor (92+), and other online sources</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-blue-400 mb-2">30+</div>
                <div className="text-lg font-semibold text-white mb-2">CO Jurisdictions</div>
                <p className="text-blue-100 text-sm">Licensed in 30+ Colorado cities including Lakewood, with all permits and inspections handled</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-green-400 mb-2">A+</div>
                <div className="text-lg font-semibold text-white mb-2">BBB Rating</div>
                <p className="text-blue-100 text-sm">Accredited 7/15/2024</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20 text-center">
                <div className="text-4xl font-bold text-orange-400 mb-2">ONLY</div>
                <div className="text-lg font-semibold text-white mb-2">Licensed Plumber</div>
                <p className="text-blue-100 text-sm">Showing Affordable Prices Online with 1000+ Verified Reviews Across All Platforms</p>
              </div>
            </div>
          </div>
        </section>

        {/* Master Plumber Attribution Box with Casa Bonita Photo */}
        <section className="py-12 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container-custom max-w-4xl">
            <div className="bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="flex-shrink-0">
                  <img 
                    src="/page-specific-content/lakewood-plumber/codee-michelle-son-at-casa-bonita-in-lakewood.jpg" 
                    alt="Codee and Michelle Jessen with their son at Casa Bonita in Lakewood - Former Lakewood residents near Kipling and Florida"
                    className="w-32 h-32 rounded-full object-contain bg-white/5 border-4 border-white/20 shadow-lg"
                  />
                </div>
                <div className="flex-grow text-center md:text-left">
                  <h3 className="text-2xl font-bold text-white mb-2">Written by <Link to="/team/codee-jessen" className="text-blue-200 hover:text-blue-100 underline">Codee Jessen</Link>, Licensed Master Plumber</h3>
                  <div className="text-blue-100 mb-4">
                    <p className="mb-2">• Colorado Master Plumber License</p>
                    <p className="mb-2">• Former Lakewood Residents (Kipling & Florida area)</p>
                    <p className="mb-2">• 18+ Years Experience • 18,000+ Jobs Completed</p>
                    <p>• Specialties: Water Heaters, Sewer Repair, Tankless, Custom Homes</p>
                  </div>
                  <p className="text-sm text-blue-200 italic">
                    "Why trust your plumbing to a Big Box franchise when you can support a local, family-owned business that called Lakewood home? 
                    We lived right here near Kipling and Florida, enjoying Casa Bonita with our kids just like you. 
                    Now in Highlands Ranch, we still serve our Lakewood neighbors with the same honest, transparent pricing that made us your trusted choice."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* How Can Others Claim "Affordable" Section */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              How Can Others Claim "Affordable" Without Showing Prices?
            </h2>
            <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <p className="text-xl mb-6 text-white font-semibold">
                We are the only Licensed Plumbing Contractor in Colorado proving affordable means transparent. True affordable plumbing shows <Link to="/water-heater-pricing" className="text-blue-200 hover:text-blue-100 underline">water heater costs</Link>, <Link to="/tankless-water-heater-pricing" className="text-blue-200 hover:text-blue-100 underline">tankless prices</Link>, <Link to="/sewer-repair-replacement" className="text-blue-200 hover:text-blue-100 underline">sewer repair rates</Link>, and more - all online before you call.
              </p>
              <p className="text-lg mb-6 text-blue-100">
                As former Lakewood residents who raised our family near Kipling and Florida, we understand the value of honest, 
                affordable service. Big franchises charge premium prices for fancy marketing - we invest in fair pricing and quality work. 
                That's why we're the only plumber showing exact prices online, proving affordable means transparent.
              </p>
              
              {/* Price Comparison Cards */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-6">
                  <h3 className="text-green-400 font-bold mb-4">✅ Our Affordable Price: $5,000 (Sewer Spot Repair)</h3>
                  <ul className="text-blue-100 space-y-2">
                    <li>✓ Exact price online</li>
                    <li>✓ No hidden fees</li>
                    <li>✓ Includes everything</li>
                    <li>✓ Written guarantee</li>
                  </ul>
                </div>
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
                  <h3 className="text-red-400 font-bold mb-4">❌ Others' "Affordable": $???</h3>
                  <ul className="text-blue-100 space-y-2">
                    <li>✗ "Free estimate"</li>
                    <li>✗ High-pressure sales</li>
                    <li>✗ Price varies by salesperson</li>
                    <li>✗ Hidden upcharges</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Services Section */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-4 text-white">
              Affordable Professional Plumbing Services in Lakewood
            </h2>
            <p className="text-center text-blue-100 mb-12 max-w-3xl mx-auto">
              From <Link to="/residential-plumbing" className="text-blue-200 hover:text-blue-100 underline">emergency repairs</Link> to complete <Link to="/bathroom-kitchen-remodel" className="text-blue-200 hover:text-blue-100 underline">home remodels</Link>, we provide <Link to="/pricing" className="text-blue-200 hover:text-blue-100 underline">affordable transparent pricing</Link> for every service
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <Link to="/water-heater-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Droplet className="w-12 h-12 text-blue-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Water Heater Replacement</h3>
                <p className="text-blue-100">Rheem and AO Smith atmospheric. 40-gallon $2,000, 50-gallon $2,100!</p>
              </Link>
              
              <Link to="/tankless-water-heater-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Zap className="w-12 h-12 text-yellow-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Tankless Water Heater</h3>
                <p className="text-blue-100">Replace existing $5,000. New installation $8,000. Navien & Rinnai.</p>
              </Link>
              
              <Link to="/sewer-repair-replacement" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Shield className="w-12 h-12 text-green-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Sewer Repair & Replacement</h3>
                <p className="text-blue-100">Spot repair $5,000, Full replacement $12,000. Camera scope $175.</p>
              </Link>
              
              <Link to="/sewer-cleaning-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Camera className="w-12 h-12 text-purple-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Sewer Cleaning & Scope</h3>
                <p className="text-blue-100">Camera inspection $175. Hydro jetting from $695. Root removal $495-$795.</p>
              </Link>
              
              <Link to="/bathroom-kitchen-remodel" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Home className="w-12 h-12 text-orange-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Remodel & Renovation</h3>
                <p className="text-blue-100">Complete bathroom & kitchen plumbing. Licensed, insured, guaranteed.</p>
              </Link>
              
              <Link to="/commercial-plumbing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Award className="w-12 h-12 text-red-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Commercial Plumbing</h3>
                <p className="text-blue-100">Restaurants, offices, retail. Licensed for commercial work throughout Colorado.</p>
              </Link>
              
              <Link to="/pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <CheckCircle className="w-12 h-12 text-teal-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Much More</h3>
                <p className="text-blue-100">Garbage disposal, faucets, valve labeling, diagnostics. All with exact pricing.</p>
              </Link>
              
              <Link to="/diagnostic-services-pricing" className="bg-white/10 backdrop-blur-sm rounded-xl p-6 hover:bg-white/20 transition-all duration-300 border border-white/20">
                <Gauge className="w-12 h-12 text-cyan-400 mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Diagnostic Services</h3>
                <p className="text-blue-100">Standard $95, Difficult $225. Annual checkup $350. Valve labeling $275.</p>
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ 1: Sewer Camera & Clog Removal */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              How Much is a Sewer Camera Inspection and Clog Removal in Lakewood?
            </h2>
            
            <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <img 
                src="/page-specific-content/lakewood-plumber/sewer-camera-locate-affordable-lakewood.jpg" 
                alt="Professional sewer camera inspection equipment in Lakewood showing affordable diagnostic services"
                className="w-full rounded-lg mb-8 shadow-lg"
                loading="lazy"
              />
              
              <p className="text-lg mb-6 text-blue-100">
                <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC offers affordable sewer diagnostics in Lakewood starting with a camera inspection for $175.</strong> Based on findings, standard mainline snaking from a proper cleanout is $295, while powerful hydro-jetting service is $695 - proving affordable doesn't mean cutting corners.
              </p>

              <h3 className="text-2xl font-bold mb-4 text-white">Why Start with a $225 Diagnostic Inspection in Lakewood?</h3>
              <p className="text-blue-100 mb-6">
                Lakewood's diverse housing ages - from mid-century homes near Green Mountain to newer developments in Belmar - means sewer problems range from simple clogs to collapsed clay pipes. Our $225 camera inspection eliminates guesswork, providing video proof of your sewer line's interior. This helps homeowners near Bear Creek Lake Park and throughout Lakewood choose the right flat-rate solution with confidence.
              </p>

              <h3 className="text-2xl font-bold mb-4 text-white">Choosing Between Snaking ($295) and Hydro-Jetting ($695)</h3>
              <p className="text-blue-100 mb-6">
                For common blockages in Lakewood's established neighborhoods like those near our old home at Kipling and Florida, our $295 mainline snaking service effectively clears the line. However, for heavy grease buildup common near Lakewood's restaurant districts or pervasive root intrusion from the mature trees throughout Green Mountain, our $695 <Link to="/hydro-jetting-pricing" className="text-blue-200 hover:text-blue-100 underline">hydro-jetting service</Link> provides comprehensive cleaning that scours pipe walls, preventing future backups.
              </p>

              <h3 className="text-2xl font-bold mb-4 text-white">Transparent Solutions for Every Lakewood Home</h3>
              <p className="text-blue-100 mb-6">
                This diagnostic-first approach ensures Lakewood residents only pay for services they truly need. Whether you're in a historic home near Lakewood Heritage Center or a newer property in Belmar, we present exact costs for all potential solutions upfront. No franchise fees, no commission-based upselling - just honest, affordable service from former Lakewood neighbors who understand this community's unique plumbing challenges.
              </p>

              <div className="mt-8 p-6 bg-blue-800/30 rounded-lg border border-blue-600/20">
                <p className="text-white font-semibold mb-2">💡 Local Tip for Lakewood Homeowners:</p>
                <p className="text-blue-100">
                  Properties near Bear Creek and older sections of Green Mountain often experience more root intrusion due to mature cottonwoods. Annual camera inspections can catch problems early when affordable repairs are still possible.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 2: Cast Iron Options */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              What Are My Options for Failing Cast Iron Sewer Pipes in Lakewood?
            </h2>
            
            <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <img 
                src="/page-specific-content/lakewood-plumber/sewer-underground-chip-up-basement-tub-and-add-pvc-ptrap.jpg" 
                alt="Cast iron sewer pipe replacement in Lakewood basement showing affordable repair options"
                className="w-full rounded-lg mb-8 shadow-lg"
                loading="lazy"
              />
              
              <p className="text-lg mb-6 text-blue-100">
                <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC provides affordable solutions for Lakewood's aging cast iron pipes with mainline descaling for $995 or full trenchless replacement for $12,000.</strong> Both options come with transparent, flat-rate pricing - no surprises, no franchise markups.
              </p>

              <h3 className="text-2xl font-bold mb-4 text-white">Understanding Our $995 Cast Iron Descaling Service</h3>
              <p className="text-blue-100 mb-6">
                Many mid-century homes near Lakewood Heritage Center and older neighborhoods around Kipling still have original cast iron pipes. If your pipes are structurally sound but suffer from heavy internal corrosion and scaling, our $995 descaling service uses specialized equipment to grind away buildup, restoring the pipe's original diameter. This affordable solution can significantly extend your existing sewer line's life without excavation.
              </p>

              <h3 className="text-2xl font-bold mb-4 text-white">When is a $12,000 Full Replacement Necessary?</h3>
              <p className="text-blue-100 mb-6">
                If camera inspection reveals your cast iron pipe has cracks, holes (channeling), or has collapsed - common in Lakewood homes built in the 1950s-60s - full replacement is the only permanent solution. Our $12,000 <Link to="/sewer-repair-replacement" className="text-blue-200 hover:text-blue-100 underline">trenchless pipe bursting method</Link> replaces old, brittle pipe with seamless modern materials, providing a 50+ year solution impervious to roots and corrosion. This affordable flat rate beats franchise quotes that often exceed $15,000-20,000.
              </p>

              <h3 className="text-2xl font-bold mb-4 text-white">Tailored Solutions for Lakewood's Older Housing Stock</h3>
              <p className="text-blue-100 mb-6">
                Having lived near Kipling and Florida, we understand Lakewood's infrastructure challenges. We provide clear, flat-rate options addressing specific issues in aging cast iron systems. Whether rehabilitation or full replacement, you'll know the exact cost before work begins - that's the difference between local family business and Big Box franchises.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ 3: Yard Excavation */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              How Much Does Excavating a Sewer Line in a Lakewood Yard Cost?
            </h2>
            
            <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <img 
                src="/page-specific-content/lakewood-plumber/outside-sewer-repair-with-double-cleanout-inspected.jpg" 
                alt="Affordable sewer line excavation and repair in a Lakewood yard with professional equipment"
                className="w-full rounded-lg mb-8 shadow-lg"
                loading="lazy"
              />
              
              <p className="text-lg mb-6 text-blue-100">
                <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC offers affordable sewer spot repair in Lakewood yards for a flat rate of $5,000.</strong> This all-inclusive price covers the entire process from excavation to restoration - proving affordable means no hidden fees or surprise charges.
              </p>

              <h3 className="text-2xl font-bold mb-4 text-white">What's Included in Your $5,000 Lakewood Yard Excavation and Repair?</h3>
              <p className="text-blue-100 mb-6">
                This affordable price covers excavation at the precise failure point, removal of damaged pipe section, installation of new high-quality piping with proper couplings, professional backfilling and compaction, and final camera inspection to verify the repair. Whether you're near Bear Creek Lake Park with its mature trees or in the established neighborhoods around Green Mountain, this complete, permanent fix addresses single problem areas without franchise overhead.
              </p>

              <h3 className="text-2xl font-bold mb-4 text-white">Minimizing Disruption to Your Established Lakewood Property</h3>
              <p className="text-blue-100 mb-6">
                A spot repair is the ideal solution for isolated issues like single breaks or root intrusion points common near Lakewood's abundant mature trees. By targeting only the damaged section, we preserve the majority of your landscaping, driveway, and yard - crucial for maintaining property values in established Lakewood neighborhoods. This affordable alternative to full replacement makes sense when the rest of your line remains in good condition.
              </p>

              <h3 className="text-2xl font-bold mb-4 text-white">An Upfront Price for a Complex Job</h3>
              <p className="text-blue-100 mb-6">
                Excavation can be intimidating for homeowners, especially when franchises won't provide prices until after "free inspections." Our $5,000 flat-rate removes all uncertainty, providing a clear, guaranteed cost for complex but sometimes necessary repairs. As former Lakewood residents, we know the value of honest pricing - that's why we show it online before you call.
              </p>

              {/* Price Comparison */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-green-500/10 border border-green-500/20 rounded-lg p-6">
                  <h3 className="text-green-400 font-bold mb-4">✅ Our Affordable Price: $5,000</h3>
                  <ul className="text-blue-100 space-y-2">
                    <li>✓ Complete excavation & repair</li>
                    <li>✓ All materials included</li>
                    <li>✓ Landscape restoration</li>
                    <li>✓ Camera verification</li>
                  </ul>
                </div>
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-6">
                  <h3 className="text-red-400 font-bold mb-4">❌ Franchise "Estimates"</h3>
                  <ul className="text-blue-100 space-y-2">
                    <li>✗ Price hidden until arrival</li>
                    <li>✗ Commission-based pricing</li>
                    <li>✗ Additional fees for everything</li>
                    <li>✗ Often $7,000-$10,000+</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 4: Water Heater Summer Special */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              What Are the Summer Special Prices for a Water Heater Replacement in Lakewood?
            </h2>
            
            <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <img 
                src="/page-specific-content/lakewood-plumber/2000-ao-smith-water-heater-special-lakewood.jpg" 
                alt="Affordable $2000 AO Smith water heater installation special in Lakewood Colorado"
                className="w-full rounded-lg mb-8 shadow-lg"
                loading="lazy"
              />
              
              <p className="text-lg mb-6 text-blue-100">
                <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC's affordable Summer Special provides complete water heater replacement for Lakewood homeowners at $2,000 for a 40-gallon unit or $2,100 for a 50-gallon unit.</strong> These all-inclusive prices prove affordable doesn't mean cutting corners or hidden fees.
              </p>

              <h3 className="text-2xl font-bold mb-4 text-white">A Truly All-Inclusive Price: What the Special Covers</h3>
              <p className="text-blue-100 mb-6">
                This flat-rate price includes the new Rheem or AO Smith <Link to="/water-heater-pricing" className="text-blue-200 hover:text-blue-100 underline">water heater</Link>, professional installation to current Lakewood plumbing codes, thermal expansion tank, drain pan, new water and gas flex lines, all necessary permits, and removal/disposal of your old tank. Whether you're in our old neighborhood near Kipling and Florida or anywhere in Lakewood, there are absolutely no hidden fees.
              </p>

              <h3 className="text-2xl font-bold mb-4 text-white">Our Standard Bradford White Option</h3>
              <p className="text-blue-100 mb-6">
                If you prefer premium Bradford White water heaters, our standard flat-rate prices remain transparently affordable: $2,425 for a 40-gallon model and $2,525 for a 50-gallon model, including the same comprehensive installation package. These prices beat franchise quotes that often hide fees until after installation.
              </p>

              <h3 className="text-2xl font-bold mb-4 text-white">Perfect for Lakewood's Hard Water Challenges</h3>
              <p className="text-blue-100 mb-6">
                Lakewood's water, sourced partly from Bear Creek, contains minerals that accelerate water heater wear. Having lived here, we understand these challenges. Our affordable installation includes proper setup for Lakewood's specific water conditions, ensuring maximum lifespan from your investment. Skip the franchise salespeople - get honest, affordable service from former neighbors.
              </p>

              <div className="mt-8 p-6 bg-green-600/20 rounded-lg border border-green-500/30">
                <p className="text-white font-bold text-xl mb-2">🏷️ Limited Time Offer!</p>
                <p className="text-blue-100">
                  Summer Special pricing ends soon. Call  to lock in these affordable rates for your Lakewood home.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ 5: Electric/Direct Vent Options */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              How Much Does an Electric or Direct Vent Water Heater Cost in Lakewood?
            </h2>
            
            <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <img 
                src="/page-specific-content/lakewood-plumber/affordable-bradford-white-40-gallon-lakewood-2400.jpg" 
                alt="Affordable Bradford White electric water heater installation in Lakewood for $5,395"
                className="w-full rounded-lg mb-8 shadow-lg"
                loading="lazy"
              />
              
              <p className="text-lg mb-6 text-blue-100">
                <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC offers affordable alternative water heater options in Lakewood with transparent flat-rate pricing: 40-gallon electric for $5,395 and 40-gallon power direct vent also for $5,395.</strong> These specialized installations require expertise that franchises often lack.
              </p>

              <h3 className="text-2xl font-bold mb-4 text-white">When is an Electric or Direct Vent Unit Necessary?</h3>
              <p className="text-blue-100 mb-6">
                Some Lakewood homes, particularly older properties near the Heritage Center or converted spaces, lack proper gas lines or chimney venting. Electric water heaters solve the no-gas-line challenge, while power direct vent units work when your water heater location isn't near a chimney - common in Lakewood's ranch-style homes. Our affordable flat-rate prices cover all complexity and materials for these specialized installations.
              </p>

              <h3 className="text-2xl font-bold mb-4 text-white">Why These Units Cost More But Save Long-Term</h3>
              <p className="text-blue-100 mb-6">
                While initial costs exceed standard atmospheric units, electric and direct vent water heaters offer advantages for specific Lakewood situations. Electric units eliminate gas line requirements and carbon monoxide concerns. Direct vent models allow flexible placement anywhere with an exterior wall. Our transparent pricing means no surprise charges when discovering your home needs these specialized solutions.
              </p>

              <h3 className="text-2xl font-bold mb-4 text-white">Expert Installation for Complex Systems</h3>
              <p className="text-blue-100 mb-6">
                These installations require electrical upgrades (for electric units) or specialized venting systems (for direct vent models). Having worked throughout Lakewood from our time living near Kipling and Florida, we understand local code requirements and home configurations. Unlike franchises that subcontract complex work, our Master Plumber team handles everything directly, ensuring affordable, quality installation.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ 6: Tankless Investment */}
        <section className="py-16 bg-gradient-to-br from-blue-800 to-blue-900 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-8 text-white">
              Is Upgrading to a Tankless Water Heater in Lakewood Worth the Cost?
            </h2>
            
            <div className="max-w-4xl mx-auto bg-white/10 backdrop-blur-sm rounded-xl p-8 border border-white/20">
              <img 
                src="/page-specific-content/lakewood-plumber/navien-water-heater-installed-with-condensing-neutralizer-lakewood.jpg" 
                alt="Professional Navien tankless water heater installation in Lakewood showing affordable upgrade option"
                className="w-full rounded-lg mb-8 shadow-lg"
                loading="lazy"
              />
              
              <p className="text-lg mb-6 text-blue-100">
                <strong className="text-yellow-300">Colorado First Plumbing and Sewer LLC provides affordable tankless water heater solutions in Lakewood at $8,000 for new installation or $5,000 for replacement.</strong> With endless hot water, up to 34% energy savings, and 20+ year lifespan, tankless systems offer excellent long-term value for Lakewood homes.
              </p>

              <h3 className="text-2xl font-bold mb-4 text-white">The Value Behind the $8,000 Tankless Conversion</h3>
              <p className="text-blue-100 mb-6">
                This all-inclusive price covers a high-efficiency Navien or Rinnai unit plus necessary upgrades to your home's venting and gas lines. For Lakewood homes near Belmar's newer developments or renovated properties throughout Green Mountain, <Link to="/tankless-water-heater-pricing" className="text-blue-200 hover:text-blue-100 underline">tankless systems</Link> provide endless hot water for multiple bathrooms while reclaiming valuable floor space. Our upfront pricing lets you budget for this premium upgrade with complete certainty - no franchise surprises.
              </p>

              <h3 className="text-2xl font-bold mb-4 text-white">Perfect for Lakewood's Modern Families</h3>
              <p className="text-blue-100 mb-6">
                Having raised our family in Lakewood near Kipling and Florida, we understand the morning rush when everyone needs hot water simultaneously. Tankless systems eliminate the "who showered last?" problem forever. With Lakewood's natural gas availability and relatively mild winters, tankless efficiency savings typically pay for themselves within 7-10 years through lower utility bills.
              </p>

              <h3 className="text-2xl font-bold mb-4 text-white">Why Choose Local Expertise Over Franchise Sales</h3>
              <p className="text-blue-100 mb-6">
                Tankless installation requires precise sizing calculations based on your home's specific needs - something franchise salespeople often miscalculate. Our Master Plumber team properly sizes units for Lakewood's 5,500-foot elevation and 40°F groundwater temperature, ensuring optimal performance. We provide honest assessments about whether tankless makes sense for your specific situation, not commission-driven upselling.
              </p>

              <div className="mt-8 p-6 bg-blue-800/30 rounded-lg border border-blue-600/20">
                <p className="text-white font-semibold mb-2">💰 ROI Calculator for Lakewood Homes:</p>
                <p className="text-blue-100">
                  Average Lakewood family saves $300-500/year on energy bills with tankless. At $8,000 initial cost, your investment pays for itself in 16-26 years through energy savings alone - while enjoying endless hot water and extra storage space today.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Common Issues Section with Local vs. Franchise Messaging */}
        <section className="py-16 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 text-white">
              Common Plumbing Issues in Lakewood (And Why We're Not a Franchise)
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center mb-4">
                  <AlertTriangle className="w-8 h-8 text-yellow-400 mr-3" />
                  <h3 className="text-xl font-bold text-white">Clay Pipe Problems (1950s-60s Homes)</h3>
                </div>
                <p className="text-blue-100 mb-4">
                  Many homes in older Lakewood neighborhoods near Heritage Center and around Kipling still have original clay sewer lines that are brittle and prone to cracking. These pipes are particularly vulnerable to root intrusion from mature cottonwoods and ground movement from Colorado's freeze-thaw cycles.
                </p>
                <p className="text-sm text-yellow-300">
                  Our Solution: $175 camera inspection identifies problems early. Spot repairs $5,000, descaling $995, full replacement $12,000.
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center mb-4">
                  <Droplet className="w-8 h-8 text-blue-400 mr-3" />
                  <h3 className="text-xl font-bold text-white">Hard Water & Mineral Buildup</h3>
                </div>
                <p className="text-blue-100 mb-4">
                  Bear Creek and South Platte water sources contain minerals causing scale buildup in water heaters and fixtures. This reduces efficiency and shortens appliance life - especially problematic in Lakewood's established neighborhoods with older plumbing systems.
                </p>
                <p className="text-sm text-green-400">
                  Our Solution: Proper water heater installation with expansion tanks. Regular maintenance prevents costly replacements.
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center mb-4">
                  <Home className="w-8 h-8 text-orange-400 mr-3" />
                  <h3 className="text-xl font-bold text-white">Root Intrusion from Mature Trees</h3>
                </div>
                <p className="text-blue-100 mb-4">
                  Green Mountain and areas near Bear Creek Lake Park have abundant mature trees whose roots seek water from sewer lines. Tree roots can completely block lines or crack pipes, causing backups and expensive emergency repairs.
                </p>
                <p className="text-sm text-purple-400">
                  Our Solution: Regular camera inspections catch root problems early. Root removal $495-$795, hydro jetting $695.
                </p>
              </div>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="flex items-center mb-4">
                  <Users className="w-8 h-8 text-teal-400 mr-3" />
                  <h3 className="text-xl font-bold text-white">Why Lakewood Chooses Local Over Franchises</h3>
                </div>
                <p className="text-blue-100 mb-4">
                  Having lived in Lakewood near Kipling and Florida, we understand this community's needs better than any franchise ever could. We've dealt with the same clay pipe issues, the same hard water from Bear Creek, and yes, we've waited in the same lines at Casa Bonita!
                </p>
                <p className="text-sm text-cyan-400">
                  Choose a plumber who knows Lakewood because we've lived it - not a franchise that treats every city the same.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Price Timestamp Innovation */}
        <section className="py-8 bg-gradient-to-br from-blue-800 to-blue-900">
          <div className="container-custom text-center">
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 border border-white/20">
              <CheckCircle className="w-5 h-5 text-green-400 mr-2" />
              <span className="text-white font-semibold">
                ✅ Prices verified: {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="py-20 bg-gradient-to-bl from-blue-900 to-blue-950 overflow-hidden">
          <div className="container-custom text-center relative z-10">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-white">
              Ready for Truly Affordable Plumbing from Former Lakewood Neighbors?
            </h2>
            <p className="text-xl mb-8 text-blue-100 max-w-3xl mx-auto">
              Skip the franchise fees and corporate markups. Get honest, affordable service from a family business that called Lakewood home.
            </p>

            {/* Final Price Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 max-w-6xl mx-auto mb-12">
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="text-3xl font-bold text-orange-400 mb-2">$5,000</div>
                <p className="text-white font-semibold">Tankless Replace</p>
                <p className="text-sm text-blue-100">Affordable existing unit replacement</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="text-3xl font-bold text-yellow-400 mb-2">$2,000</div>
                <p className="text-white font-semibold">40-Gal Water Heater</p>
                <p className="text-sm text-blue-100">Summer Special - all included</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="text-3xl font-bold text-green-400 mb-2">$175</div>
                <p className="text-white font-semibold">Sewer Camera Scope</p>
                <p className="text-sm text-blue-100">Complete video inspection</p>
              </div>
              <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 border border-white/20">
                <div className="text-3xl font-bold text-purple-400 mb-2">$5,000</div>
                <p className="text-white font-semibold">Sewer Spot Repair</p>
                <p className="text-sm text-blue-100">All-inclusive yard excavation</p>
              </div>
            </div>

            {/* Final CTAs */}
            <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
              
              <Link 
                to="/pricing" 
                className="bg-gradient-to-r from-success-primary to-success-eco hover:shadow-xl hover:scale-[1.02] text-white px-12 py-6 rounded-full text-2xl font-semibold inline-block transition-all duration-300"
              >
                💰 See All Affordable Prices
              </Link>
            </div>

            <div className="mt-12 max-w-2xl mx-auto">
              <div className="bg-white/5 backdrop-blur-sm rounded-xl p-6 border border-white/10">
                <p className="text-white font-semibold mb-2">🏡 Proudly Serving Former Neighbors</p>
                <p className="text-blue-100 text-sm">
                  From our old home near Kipling and Florida to our current base in Highlands Ranch, 
                  we're still your local Lakewood plumbers - just with better Casa Bonita memories and even more experience!
                </p>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default LakewoodPlumber;